import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Form, Input, Button, Card, Typography, message, Space, Result } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { api } from '../lib/api';

const { Text } = Typography;

export default function ResetPasswordPage() {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const onFinish = async (values: { password: string }) => {
    if (!token) {
      message.error('Reset token is missing.');
      return;
    }
    try {
      setLoading(true);
      await api.auth.resetPassword(token, values.password);
      setDone(true);
    } catch (error: any) {
      message.error(error.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const primaryColor = '#f46524';
  const cardStyle = {
    width: 420,
    maxWidth: '100%',
    borderRadius: 12,
    border: `1px solid ${primaryColor}`,
    boxShadow: '0 12px 35px rgba(244, 101, 36, 0.25)',
    background: '#fffdfa',
    overflow: 'hidden',
  };

  if (done) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          background: '#f5f5f5',
        }}
      >
        <Card style={cardStyle}>
          <Result
            status="success"
            title="Password Updated"
            subTitle="You can now sign in with your new password."
            extra={
              <Link to="/login">
                <Button type="primary">Back to Login</Button>
              </Link>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: '#f5f5f5',
      }}
    >
      <Card
        title="Set New Password"
        style={cardStyle}
        headStyle={{
          color: primaryColor,
          fontWeight: 600,
          textAlign: 'center',
          background: '#fffdfa',
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
        }}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <Text type="secondary">Enter a new password for your account</Text>
          </div>

          <Form
            name="reset-password"
            onFinish={onFinish}
            autoComplete="off"
            layout="vertical"
            requiredMark={false}
          >
            <Form.Item
              name="password"
              label="New Password"
              rules={[
                { required: true, message: 'Required' },
                { min: 6, message: 'Password must be at least 6 characters' },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="New password"
              />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label="Confirm Password"
              dependencies={['password']}
              rules={[
                { required: true, message: 'Required' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match'));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Confirm new password"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                block
                loading={loading}
              >
                Reset Password
              </Button>
            </Form.Item>
          </Form>

          {!token && (
            <Text type="danger">
              Reset token is missing. Please request a new reset link.
            </Text>
          )}
        </Space>
      </Card>
    </div>
  );
}
